import React, {useEffect} from "react";
import {Outlet, useLocation, useNavigate} from "react-router-dom";

import {Header, Title, TopBar} from "@/components";
import {TopBarItem} from "@/components/TopBar/types";

import {useCurrentWidth} from "@/hooks";
import {MediaBreakPoints} from "@/resources/constants";

import {useAppDispatch} from "@redux/store";
import {fetchGetNews} from "@redux/News/asyncActions";

import {AboutPropsType} from "./types";
import styles from "./About.module.scss"

import galleryIcon from "@assets/icons/about/gallery.svg";
import contactsIcon from "@assets/icons/about/contacts.svg";
import routingIcon from "@assets/icons/about/routing.svg";
import courtSchemeIcon from "@assets/icons/about/court-scheme.svg";
import newsIcon from "@assets/icons/about/news.svg";
import messagesIcon from "@assets/icons/about/messages.svg";
import offertIcon from "@assets/icons/about/offert.svg";
import policyIcon from "@assets/icons/about/policy.svg";
import logo from "@assets/img/logo.svg";
import greenArrow from "@assets/icons/arrowCalendarRightGreen.svg";

export const About: React.FC<AboutPropsType> = ({ title }) => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const windowWidth = useCurrentWidth();
	const breakPoint = MediaBreakPoints.MOBILE_LARGE;

	// Десктоп — только внутренние страницы /about/*
	const desktopItems: TopBarItem[] = [
		{ id: "photo", text: "Галерея", path: "photo", icon: galleryIcon },
		{ id: "direction", text: "Схема проезда", path: "direction", icon: routingIcon },
		{ id: "court-layout", text: "Схема кортов", path: "court-layout", icon: courtSchemeIcon },
		{ id: "news", text: "Новости", path: "news", icon: newsIcon },
	];

	// Мобилка — все 8 пунктов как в мобильном приложении
	const mobileItems = [
		{ id: "photo", text: "Галерея", path: "/about/photo", icon: galleryIcon },
		{ id: "contacts", text: "Контакты", path: "/contacts", icon: contactsIcon },
		{ id: "direction", text: "Схема проезда", path: "/about/direction", icon: routingIcon },
		{ id: "court-layout", text: "Схема кортов", path: "/about/court-layout", icon: courtSchemeIcon },
		{ id: "news", text: "Новости", path: "/about/news", icon: newsIcon },
		{ id: "feedback", text: "Обратная связь", path: "/feedback", icon: messagesIcon },
		{ id: "offert", text: "Оферта и правила клуба", path: "/rules", icon: offertIcon },
		{ id: "policy", text: "Политика конфиденциальности", path: "/privacy-policy", icon: policyIcon },
	];

	useEffect(() => {
		if (location.pathname === "/about/news") {
			dispatch(fetchGetNews());
		}
	}, [location.pathname, dispatch]);

	const handleDesktopItemClick = (item: TopBarItem) => {
		if (item.id === "news") {
			dispatch(fetchGetNews());
		}
	};

	const handleMobileItemClick = (item: typeof mobileItems[0]) => {
		if (item.id === "news") {
			dispatch(fetchGetNews());
		}
		navigate(item.path);
	};

	const currentTitle = desktopItems.find(item =>
		location.pathname.includes(`/about/${item.path}`)
	)?.text || title;

	const isMobile = windowWidth <= breakPoint;
	const isAboutRoot = location.pathname === "/about" || location.pathname === "/about/";

	return (
		<>
			<Header title={currentTitle} />
			<main>
				<section className="container">
					<Title>{title}</Title>

					{isAboutRoot && (
						<div className={styles.logoContainer}>
							<img src={logo} alt="Tennis.ru" className={styles.logo} />
						</div>
					)}

					{isMobile ? (
						isAboutRoot ? (
							<nav className={styles.mobileMenu}>
								{mobileItems.map((item) => (
									<div
										key={item.id}
										className={styles.mobileMenuItem}
										onClick={() => handleMobileItemClick(item)}
									>
										<div className={styles.mobileMenuLeft}>
											<img src={item.icon} alt="" className={styles.mobileMenuIcon} />
											<span className={styles.mobileMenuText}>{item.text}</span>
										</div>
										<img src={greenArrow} alt="" className={styles.mobileMenuArrow} />
									</div>
								))}
							</nav>
						) : (
							<div className={styles.wrapper}>
								<Outlet />
							</div>
						)
					) : (
						<div className={styles.wrapper}>
							<TopBar
								items={desktopItems}
								basePath="/about"
								onItemClick={handleDesktopItemClick}
								breakPoint={breakPoint}
							/>
							<div className={styles.content}>
								<Outlet />
							</div>
						</div>
					)}
				</section>
			</main>
		</>
	);
};
